import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../components/landing/Navbar';
import Footer from '../components/landing/Footer';

/**
 * Reusable layout for long-form legal/policy pages (Privacy, Terms).
 * Accepts structured copy from translations.
 */
export default function LegalPage({
  testId,
  eyebrow,
  title,
  titleAccent,
  effective,
  intro,
  sections,
  backLabel,
  contactPre,
  contactEmail,
}) {
  return (
    <div className="min-h-screen bg-black" data-testid={testId}>
      <Navbar />

      <section className="pt-40 pb-16 md:pt-48 md:pb-20 border-b border-hb-gray-border">
        <div className="max-w-[1100px] mx-auto px-6 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.25em] text-hb-gray hover:text-white transition-colors mb-10"
              data-testid={`${testId}-back-link`}>
              <span>&lsaquo;</span> {backLabel}
            </Link>
            <p className="font-body text-[10px] uppercase tracking-[0.4em] text-hb-green mb-6">{eyebrow}</p>
            <h1 className="font-heading font-normal text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white mb-6">
              {title} <em className="text-hb-green">{titleAccent}</em>
            </h1>
            {effective && (
              <p className="font-body text-xs uppercase tracking-[0.3em] text-hb-gray-dark">{effective}</p>
            )}
            {intro && (
              <p className="font-body text-base md:text-lg text-hb-gray-light leading-relaxed max-w-[760px] mt-8">{intro}</p>
            )}
          </motion.div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-8">
          <div className="space-y-16 md:space-y-20">
            {sections.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5 }}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-start"
                data-testid={`${testId}-section-${i}`}>
                <div className="md:col-span-3">
                  <div className="flex items-baseline gap-3">
                    <span className="font-heading italic text-2xl text-hb-green leading-none">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="h-px flex-1 bg-hb-gray-border mt-3" />
                  </div>
                  <h2 className="font-heading font-normal text-xl md:text-2xl leading-snug text-white mt-4">
                    {s.title}
                  </h2>
                </div>
                <div className="md:col-span-9 space-y-4">
                  {s.body.map((para, k) => (
                    <p key={k} className="font-body text-base text-hb-gray-light leading-relaxed">
                      {para}
                    </p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Contact footer */}
          {contactEmail && (
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="border-t border-hb-gray-border mt-24 pt-10 max-w-[760px]"
              data-testid={`${testId}-contact`}>
              <p className="font-body text-sm text-hb-gray leading-relaxed">
                {contactPre}{' '}
                <a
                  href={`mailto:${contactEmail}`}
                  className="text-white hover:text-hb-green underline underline-offset-4 transition-colors">
                  {contactEmail}
                </a>.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
